import React, { useEffect } from 'react'
import styled from 'styled-components'
import { BellOutlined, ReloadOutlined } from '@ant-design/icons'

import { alertsLoad } from '../../store/actions/alerts'
import { useActions, useAppSelector } from '../../store/hooks'
import { alertsSelector } from '../../store/slices/alerts'

import { AlertsPanel } from './AlertsPanel'

const BellContainer = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 12px;
    transition: background 0.2s;
    background: transparent;
    min-width: 44px;
    min-height: 44px;

    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }
`

const BellIcon = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    line-height: 1;
    color: #31456a;
`

const Badge = styled.div`
    position: absolute;
    top: -8px;
    right: -4px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    background: ${({ $level }) =>
        $level === 'error' ? '#C00000' :
            $level === 'warning' ? '#BF8F00' :
                '#2E74B5'};
    color: white;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 10;
    pointer-events: none;
`

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    margin-left: 4px;
    border-radius: 8px;
    transition: all 0.2s;
    color: #31456a;
    font-size: 16px;

    &:hover {
        transform: rotate(45deg);
    }

    &:active {
        transform: rotate(90deg);
    }

    ${({ $loading }) => $loading && `
        animation: spin 1s linear infinite;
        pointer-events: none;
        opacity: 0.6;
    `}

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
`

const alertsBellActions = { alertsLoad }

const AlertsBell = () => {
    const alerts = useAppSelector(alertsSelector.items)
    const loading = useAppSelector(alertsSelector.isLoading)
    const refreshing = useAppSelector(alertsSelector.isRefreshing)
    const summary = useAppSelector(alertsSelector.summary)
    const { alertsLoad: loadAlertsAction } = useActions(alertsBellActions)

    const [isOpen, setIsOpen] = React.useState(false)

    const loadAlerts = (refresh = false) => {
        loadAlertsAction({ refresh })
    }

    useEffect(() => {
        loadAlerts(false)
    }, [loadAlertsAction])

    const hasUnread = summary.unread > 0
    const highestLevel = summary.byLevel.error > 0 ? 'error' :
        summary.byLevel.warning > 0 ? 'warning' :
            summary.byLevel.info > 0 ? 'info' :
                'info'

    const togglePanel = () => setIsOpen(!isOpen)
    const closePanel = () => setIsOpen(false)

    const handleRefresh = (e) => {
        e.stopPropagation()
        loadAlerts(true)
    }

    return (
        <>
            <BellContainer onClick={togglePanel}>
                <BellIcon>
                    <BellOutlined style={{ fontSize: '22px', color: '#31456a' }} />
                    {hasUnread && (
                        <Badge $level={highestLevel}>
                            {summary.unread}
                        </Badge>
                    )}
                </BellIcon>

                <RefreshButton
                    $loading={refreshing}
                    onClick={handleRefresh}
                    title="Обновить уведомления"
                >
                    <ReloadOutlined />
                </RefreshButton>
            </BellContainer>

            {isOpen && (
                <AlertsPanel
                    onClose={closePanel}
                    alerts={alerts}
                    loading={loading}
                    summary={summary}
                    onRefresh={() => loadAlerts(true)}
                />
            )}
        </>
    )
}

export { AlertsBell }
export default AlertsBell
